/*
 * useAiQuota — lightweight hook to read & refresh the workspace AI quota.
 *
 * Backend remains the source of truth (it raises 403 ai_quota_reached even
 * if the frontend lies). This hook only powers UX: showing a usage badge
 * and disabling Generate buttons before the user wastes a click.
 */

import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';

export function useAiQuota() {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/seo/quota');
      setQuota(data || null);
    } catch {
      // Endpoint may not exist on legacy backends; keep UI usable.
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Quota is reached when remaining hits 0 (treating null/undefined as "open"
  // so a missing backend can't accidentally lock the user out).
  const isReached = !!quota && quota.total > 0 && quota.remaining === 0;

  return { quota, loading, refresh, isReached };
}
